import type { Context } from 'react';
import { createContext, useContext } from 'react';
import type { FormikContextType } from 'formik';
import * as FormikNS from 'formik';

/**
 * Custom hook to safely access Formik context.
 * Returns undefined if no parent Formik context is available,
 * preventing crashes when the component is used outside of a Formik provider.
 *
 * @template T - The type of the Formik form values
 * @returns FormikContextType<T> | undefined - The Formik context or undefined if not available
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const formikContext = useOptionalFormikContext<MyFormData>();
 *   const values = formikContext?.values;
 *
 *   return (
 *     <div>
 *       {values ? `Form has ${Object.keys(values).length} fields` : 'No form context'}
 *     </div>
 *   );
 * }
 * ```
 */
// Fallback context to keep hook calls consistent when FormikContext is not available (e.g., in certain tests)
const FallbackFormikContext: Context<unknown> = createContext<unknown>(undefined);

export function useOptionalFormikContext<T>(): FormikContextType<T> | undefined {
  // Resolve FormikContext dynamically via safe property access (works with test mocks that omit it)
  let maybeContext: Context<FormikContextType<T>> | undefined;
  try {
    const ns = FormikNS as unknown as Record<string, unknown>;
    maybeContext = ns['FormikContext'] as Context<FormikContextType<T>> | undefined;
  } catch {
    maybeContext = undefined;
  }

  // Always call useContext with a valid context to satisfy hook rules
  const resolvedContext =
    (maybeContext as Context<FormikContextType<T>> | undefined) ?? (FallbackFormikContext as unknown as Context<FormikContextType<T>>);

  return useContext(resolvedContext) as FormikContextType<T> | undefined;
}
